import { Injectable } from '@angular/core';
import { CommonLookupServiceProxy, WorkItemUiConfig } from '@shared/service-proxies/service-proxies';

/**
 * Strictly-typed, default-resolved view of a single entity type's UI config.
 * Never null; absent/undefined flags resolve to their backward-compatible default (ON).
 * Keeps the default-ON resolution in one typed place so callers read plain booleans.
 */
export class ResolvedWorkItemUiConfig {
    constructor(private readonly raw?: WorkItemUiConfig) {}

    /** True unless this type's search menu was explicitly disabled. */
    get isSearchMenuEnabled(): boolean {
        return this.raw?.enableSearchMenu !== false;
    }

    /** True unless this type's manage page was explicitly disabled. */
    get isManagePageEnabled(): boolean {
        return this.raw?.enableManagePage !== false;
    }
}

/**
 * Eager, synchronous, type-level UI-config store. Loaded once at main-app (VBO/Host)
 * bootstrap from GetWorkItemUiConfigMap. Read synchronously by global chrome (the nav menu)
 * via {@link getUiConfig}. Per-configId / instance-runtime config stays on the lazy
 * WorkItemUiConfigCacheService. Fail-open: any missing type or pre-load read is treated as enabled.
 */
@Injectable({ providedIn: 'root' })
export class WorkItemUiConfigStore {
    private map: { [entityType: string]: WorkItemUiConfig } = {};
    private loaded = false;

    constructor(private _commonLookup: CommonLookupServiceProxy) {}

    async load(): Promise<void> {
        try {
            const result = await this._commonLookup.getWorkItemUiConfigMap().toPromise();
            this.map = result || {};
        } catch (e) {
            console.error('[WorkItemUiConfigStore] Failed to load UI config map; failing open.', e);
            this.map = {};
        } finally {
            this.loaded = true;
        }
    }

    /**
     * Strictly-typed, default-resolved UI config for an entity type. Never null:
     * an unknown type (or a read before load) yields all-default (enabled) flags.
     */
    getUiConfig(entityType: string): ResolvedWorkItemUiConfig {
        return new ResolvedWorkItemUiConfig(this.map[entityType]);
    }
}
